import { Modal, Input, Button, Tooltip, Divider, Space, Switch, message } from 'antd';
import { AiSparkIcon } from '../components/AiSparkIcon.tsx';
import { rewriteText, streamChat } from '../../features/ai/api.ts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  CheckSquareOutlined,
  CodeOutlined,
  LinkOutlined,
  UndoOutlined,
  RedoOutlined,
  EyeOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { Markdown } from 'tiptap-markdown';
import { ResizableImage } from './resizableImage.ts';
import { TextColor, Highlight } from './textColor.ts';
import { ColorPickerButton } from './ColorPickerButton.tsx';
import { makeImagePasteProps, type ImageUploader } from './imagePaste.ts';
import './markdown-field.css';
import './markdown-editor.css';

interface MarkdownEditorModalProps {
  open: boolean;
  value: string;
  title?: string;
  /** 'markdown' включает Tiptap-WYSIWYG с тулбаром; 'textarea' — простой Input.TextArea без preview. */
  mode?: 'markdown' | 'textarea';
  /** Имя поля — попадёт в AI-промпт для контекста («Поле: …»). */
  fieldName?: string;
  /** ID записи. Если передан — вместо одиночного «Переписать» показывается полноценный чат с LLM,
   *  в контекст которого попадает вся запись (все поля + комментарии). */
  itemId?: string;
  /**
   * Если true — рендерится не как `<Modal>`, а как полноразмерный inline-блок
   * (для использования внутри родительского Drawer/Modal без вложенного оверлея).
   * Шапка с заголовком и «Назад / Сохранить» рендерится внутри.
   */
  embedded?: boolean;
  /** Если задан — вставка/дроп изображений загружает их и вставляет в текст. */
  onUploadImage?: ImageUploader;
  onSave: (value: string) => void;
  onClose: () => void;
  /**
   * Текущий текст на каждое изменение. Нужен родителю, чтобы сохранить
   * набранное, если окно закрыли мимо кнопки «Сохранить» — например кликом
   * по затемнённой зоне вокруг карточки записи.
   */
  onDraftChange?: (value: string) => void;
}

/**
 * Модальный редактор многострочного текста.
 *
 * Для markdown-полей по умолчанию показан Tiptap-WYSIWYG с тулбаром
 * (заголовки, списки, цитаты, ссылки, inline-code). По кнопке «Код»
 * переключается в режим plain markdown — тогда видно raw-разметку и
 * можно править вручную (для специфических случаев типа таблиц/HTML).
 *
 * Конвертацию между markdown ↔ ProseMirror-документом делает
 * `tiptap-markdown` (см. `Markdown.configure`); это позволяет открывать
 * существующие markdown-значения и сохранять обратно строкой.
 */
export function MarkdownEditorModal({
  open,
  value,
  title,
  mode = 'markdown',
  fieldName,
  itemId,
  embedded = false,
  onUploadImage,
  onSave,
  onClose,
  onDraftChange,
}: MarkdownEditorModalProps): JSX.Element {
  const isMarkdown = mode === 'markdown';
  // В ref, а не в замыкание: редактор создаётся один раз на открытие, иначе
  // onUpdate поймает устаревший колбэк (как onBlurRef в MarkdownInlineEditor).
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const [showCode, setShowCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState(value);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  type ChatMessage =
    | { role: 'user'; content: string }
    | { role: 'assistant'; content: string }
    | { role: 'tool'; toolCallId: string; toolName: string; status: 'running' | 'success' | 'error'; summary?: string };
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatStreaming, setChatStreaming] = useState(false);
  const [enableTools, setEnableTools] = useState(false);
  const chatAbortRef = useRef<AbortController | null>(null);
  // Ссылка на «истинное» значение, актуальное на момент save.
  // В WYSIWYG-режиме читается из editor; в code-режиме — из codeDraft.
  const draftRef = useRef(value);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ codeBlock: { HTMLAttributes: { class: 'lens-code-block' } } }),
        Placeholder.configure({ placeholder: 'Начните писать или вставьте текст…' }),
        TaskList.configure({ HTMLAttributes: { class: 'lens-task-list' } }),
        TaskItem.configure({ nested: true }),
        ResizableImage.configure({ inline: false, HTMLAttributes: { class: 'lens-md-img' } }),
        TextColor,
      Highlight,
      Markdown.configure({ html: true, transformPastedText: true, transformCopiedText: true }),
      ],
      content: value,
      editable: isMarkdown,
      editorProps: onUploadImage ? makeImagePasteProps(onUploadImage) : undefined,
      onUpdate: ({ editor: ed }) => {
        const md = (ed.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.() ?? ed.getText();
        draftRef.current = md;
        onDraftChangeRef.current?.(md);
      },
    },
    [open], // recreate editor on each modal open to avoid stale state
  );

  useEffect(() => {
    if (!open) return;
    setShowCode(false);
    setCodeDraft(value);
    setAiInstruction('');
    setChatMessages([]);
    setChatInput('');
    setEnableTools(false);
    chatAbortRef.current?.abort();
    chatAbortRef.current = null;
    setChatStreaming(false);
    draftRef.current = value;
    if (editor && isMarkdown) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [open, value, editor, isMarkdown]);

  const getCurrentText = (): string => {
    if (!isMarkdown) return codeDraft;
    if (showCode) return codeDraft;
    return (editor?.storage as { markdown?: { getMarkdown?: () => string } } | undefined)?.markdown?.getMarkdown?.() ?? draftRef.current;
  };

  const setCurrentText = (next: string) => {
    draftRef.current = next;
    setCodeDraft(next);
    if (isMarkdown && !showCode && editor) {
      editor.commands.setContent(next || '', { emitUpdate: false });
    }
  };

  const handleChatSend = async () => {
    if (!itemId) return;
    const text = chatInput.trim();
    if (!text || chatStreaming) return;
    const priorHistory = chatMessages
      .filter((m): m is { role: 'user' | 'assistant'; content: string } => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));
    setChatMessages((prev) => [...prev, { role: 'user', content: text }]);
    setChatInput('');
    setChatStreaming(true);
    const controller = new AbortController();
    chatAbortRef.current = controller;
    const fieldHint = fieldName
      ? `\n\n[Контекст редактора: пользователь сейчас редактирует поле «${fieldName}». Текущее значение поля доступно в КОНТЕКСТЕ записи. Если просят что-то применить «к этому полю» — имеется в виду именно оно.]`
      : '';

    const appendDelta = (chunk: string) => {
      setChatMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant') {
          return [...prev.slice(0, -1), { ...last, content: last.content + chunk }];
        }
        return [...prev, { role: 'assistant', content: chunk }];
      });
    };

    try {
      await streamChat(
        {
          scope: { kind: 'item', itemId },
          message: text + fieldHint,
          history: priorHistory,
          ...(enableTools ? { enableTools: true } : {}),
        },
        {
          onDelta: (chunk) => appendDelta(chunk),
          onToolCall: (toolCallId, name) => {
            setChatMessages((prev) => [
              ...prev,
              { role: 'tool', toolCallId, toolName: name, status: 'running' },
            ]);
          },
          onToolResult: (toolCallId, name, success, summary) => {
            setChatMessages((prev) =>
              prev.map((m) =>
                m.role === 'tool' && m.toolCallId === toolCallId
                  ? { ...m, toolName: name, status: success ? 'success' : 'error', summary }
                  : m,
              ),
            );
          },
          onDone: () => {
            setChatStreaming(false);
            chatAbortRef.current = null;
          },
          onError: (_code, msg) => {
            appendDelta(`_Ошибка: ${msg ?? 'не удалось получить ответ'}_`);
            setChatStreaming(false);
            chatAbortRef.current = null;
          },
        },
        controller.signal,
      );
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setChatStreaming(false);
      }
    }
  };

  const handleAiRewrite = async () => {
    const instruction = aiInstruction.trim();
    if (!instruction || aiLoading) return;
    setAiLoading(true);
    try {
      const current = getCurrentText();
      const result = await rewriteText(current, instruction, { fieldName, isMarkdown });
      setCurrentText(result.text);
      setAiInstruction('');
    } catch {
      void message.error('Lensy не смог переписать текст');
    } finally {
      setAiLoading(false);
    }
  };

  const handleOk = () => {
    let next: string;
    if (!isMarkdown) {
      next = codeDraft;
    } else if (showCode) {
      next = codeDraft;
    } else {
      next = (editor?.storage as { markdown?: { getMarkdown?: () => string } } | undefined)?.markdown?.getMarkdown?.() ?? draftRef.current;
    }
    onSave(next);
    onClose();
  };

  const toggleCodeView = () => {
    if (!editor) {
      setShowCode((v) => !v);
      return;
    }
    if (!showCode) {
      // WYSIWYG → code: serialize current state to markdown
      const md = (editor.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.() ?? '';
      setCodeDraft(md);
      setShowCode(true);
    } else {
      // code → WYSIWYG: pick up edits made in textarea
      editor.commands.setContent(codeDraft || '', { emitUpdate: false });
      setShowCode(false);
    }
  };

  const editorPane = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      {isMarkdown && editor && !showCode && <EditorToolbar editor={editor} />}
      <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
        {!isMarkdown || showCode ? (
          <Input.TextArea
            value={codeDraft}
            onChange={(e) => { setCodeDraft(e.target.value); onDraftChangeRef.current?.(e.target.value); }}
            autoFocus
            style={{
              flex: 1,
              height: '100%',
              resize: 'none',
              fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
              fontSize: 13,
            }}
            placeholder={isMarkdown ? 'Введите markdown…' : 'Введите текст…'}
          />
        ) : (
          <div
            className="lens-tiptap-wrap markdown-field"
            style={{ flex: 1, overflow: 'auto' }}
            onMouseDown={(e) => {
              // Клик по padding'у обёртки или пустому месту ниже последнего параграфа
              // не доходит до ProseMirror и не ставит курсор. Если фокус ещё не в редакторе —
              // форсим focus в конец документа.
              if (!editor || editor.isFocused) return;
              const target = e.target as HTMLElement;
              if (target.classList.contains('lens-tiptap-wrap') || target.classList.contains('ProseMirror')) {
                e.preventDefault();
                editor.commands.focus('end');
              }
            }}
          >
            <EditorContent editor={editor} />
          </div>
        )}
      </div>
      {isMarkdown && (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="text"
            size="small"
            icon={showCode ? <EyeOutlined /> : <CodeOutlined />}
            onClick={toggleCodeView}
          >
            {showCode ? 'Визуальный редактор' : 'Показать код'}
          </Button>
        </div>
      )}
    </div>
  );

  const chatPane = itemId ? (
    <div
      style={{
        width: 360,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--color-border)',
        paddingLeft: 12,
        minHeight: 0,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <AiSparkIcon /> Lensy — чат по записи
        <Tooltip title="Разрешить Lensy выполнять действия: создавать, обновлять и удалять записи">
          <Space size={4} style={{ marginLeft: 'auto' }}>
            <ThunderboltOutlined style={{ color: enableTools ? '#faad14' : '#d9d9d9', fontSize: 13 }} />
            <Switch size="small" checked={enableTools} onChange={setEnableTools} disabled={chatStreaming} />
          </Space>
        </Tooltip>
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          marginBottom: 8,
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          padding: 8,
          background: 'var(--color-bg-layout)',
          fontSize: 13,
          minHeight: 0,
        }}
      >
        {chatMessages.length === 0 ? (
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
            Спросите что угодно про эту запись. В контекст попадают все её поля и последние комментарии. Ответы можно одной кнопкой подставить в текущее поле.
            {enableTools && (
              <div style={{ marginTop: 8, padding: '6px 8px', background: 'var(--color-warning-bg)', borderRadius: 6, border: '1px solid var(--color-warning)', color: 'var(--color-warning)' }}>
                <ThunderboltOutlined style={{ color: '#faad14', marginRight: 4 }} />
                Режим действий включён — Lensy может изменять данные.
              </div>
            )}
          </div>
        ) : (
          <>
            {chatMessages.map((msg, idx) => {
              if (msg.role === 'tool') {
                const color = msg.status === 'error' ? 'var(--color-error)' : msg.status === 'success' ? 'var(--color-success)' : 'var(--color-text-secondary)';
                const icon = msg.status === 'running' ? '⏳' : msg.status === 'error' ? '⚠️' : '✓';
                return (
                  <div key={idx} style={{ marginBottom: 8, fontSize: 12, color, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span>{icon}</span>
                    <span>
                      <code style={{ background: 'var(--color-border)', padding: '0 4px', borderRadius: 3 }}>{msg.toolName}</code>
                      {msg.summary ? <> — {msg.summary}</> : null}
                    </span>
                  </div>
                );
              }
              return (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 11,
                      color: msg.role === 'user' ? 'var(--color-primary)' : '#9C6BFF',
                      marginBottom: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {msg.role === 'user' ? 'Вы' : <><AiSparkIcon /> Lensy</>}
                  </div>
                  <div className="ai-markdown" style={{ lineHeight: 1.5 }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </div>
                  {msg.role === 'assistant' && msg.content && !chatStreaming && (
                    <div style={{ marginTop: 4 }}>
                      <Button
                        size="small"
                        type="link"
                        style={{ paddingLeft: 0, fontSize: 12 }}
                        onClick={() => setCurrentText(msg.content)}
                      >
                        Применить к полю
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {chatStreaming && (() => {
              const last = chatMessages[chatMessages.length - 1];
              if (!last) return null;
              if (last.role === 'tool' && last.status === 'running') return null;
              if (last.role === 'assistant' && last.content) return null;
              return <div style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>генерация…</div>;
            })()}
          </>
        )}
      </div>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          prefix={<AiSparkIcon />}
          placeholder="Например: «напиши описание по DoD»"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onPressEnter={handleChatSend}
          disabled={chatStreaming}
        />
        <Button
          icon={<AiSparkIcon />}
          loading={chatStreaming}
          onClick={handleChatSend}
          disabled={!chatInput.trim()}
        >
          Отправить
        </Button>
      </Space.Compact>
    </div>
  ) : (
    <div
      style={{
        width: 360,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--color-border)',
        paddingLeft: 12,
        minHeight: 0,
        gap: 8,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        <AiSparkIcon /> Lensy
      </div>
      <div style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
        Однократная инструкция перепишет текущий текст. Чат с контекстом записи доступен только при редактировании существующей записи.
      </div>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          prefix={<AiSparkIcon />}
          placeholder="«сделай короче», «добавь чек-лист DoD»…"
          value={aiInstruction}
          onChange={(e) => setAiInstruction(e.target.value)}
          onPressEnter={handleAiRewrite}
          disabled={aiLoading}
        />
        <Button
          icon={<AiSparkIcon />}
          loading={aiLoading}
          onClick={handleAiRewrite}
          disabled={!aiInstruction.trim()}
        >
          Переписать
        </Button>
      </Space.Compact>
    </div>
  );

  const editorBody = (
    <div
      style={{
        display: 'flex',
        gap: 12,
        flex: embedded ? 1 : undefined,
        height: embedded ? undefined : '60vh',
        minHeight: 0,
      }}
    >
      {editorPane}
      {chatPane}
    </div>
  );

  if (embedded) {
    if (!open) return <></>;
    const headerTitle = title ?? (isMarkdown ? 'Редактирование Markdown' : 'Редактирование текста');
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          background: 'var(--color-bg-container)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 0 12px',
            borderBottom: '1px solid var(--color-border)',
            marginBottom: 12,
            gap: 8,
          }}
        >
          <Button onClick={onClose}>← Назад</Button>
          <span style={{ fontWeight: 600, flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {headerTitle}
          </span>
          <Button type="primary" onClick={handleOk}>Сохранить</Button>
        </div>
        {editorBody}
      </div>
    );
  }

  return (
    <Modal
      open={open}
      title={title ?? (isMarkdown ? 'Редактирование Markdown' : 'Редактирование текста')}
      onOk={handleOk}
      onCancel={onClose}
      okText="Сохранить"
      cancelText="Отмена"
      width={isMarkdown ? 1200 : 1100}
      destroyOnClose
    >
      {editorBody}
    </Modal>
  );
}

function EditorToolbar({ editor }: { editor: Editor }): JSX.Element {
  const btn = (
    key: string,
    icon: React.ReactNode,
    tip: string,
    cmd: () => void,
    isActive?: () => boolean,
  ) => (
    <Tooltip title={tip} key={key}>
      <Button
        size="small"
        type={isActive?.() ? 'primary' : 'text'}
        icon={icon}
        onClick={() => {
          cmd();
          editor.chain().focus().run();
        }}
      />
    </Tooltip>
  );

  return (
    <div className="lens-md-toolbar">
      {btn('bold', <BoldOutlined />, 'Жирный (Ctrl+B)', () => editor.chain().focus().toggleBold().run(), () => editor.isActive('bold'))}
      {btn('italic', <ItalicOutlined />, 'Курсив (Ctrl+I)', () => editor.chain().focus().toggleItalic().run(), () => editor.isActive('italic'))}
      {btn('underline', <UnderlineOutlined />, 'Подчёркнутый (Ctrl+U)', () => editor.chain().focus().toggleUnderline().run(), () => editor.isActive('underline'))}
      {btn('strike', <StrikethroughOutlined />, 'Зачёркнутый', () => editor.chain().focus().toggleStrike().run(), () => editor.isActive('strike'))}
      {btn('code', <CodeOutlined />, 'Inline-код', () => editor.chain().focus().toggleCode().run(), () => editor.isActive('code'))}
      <ColorPickerButton editor={editor} kind="text" />
      <ColorPickerButton editor={editor} kind="highlight" />
      <Divider type="vertical" style={{ margin: '0 4px' }} />
      {btn('h1', <span style={{ fontWeight: 600 }}>H1</span>, 'Заголовок 1', () => editor.chain().focus().toggleHeading({ level: 1 }).run(), () => editor.isActive('heading', { level: 1 }))}
      {btn('h2', <span style={{ fontWeight: 600 }}>H2</span>, 'Заголовок 2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), () => editor.isActive('heading', { level: 2 }))}
      {btn('h3', <span style={{ fontWeight: 600 }}>H3</span>, 'Заголовок 3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), () => editor.isActive('heading', { level: 3 }))}
      <Divider type="vertical" style={{ margin: '0 4px' }} />
      {btn('ul', <UnorderedListOutlined />, 'Маркированный список', () => editor.chain().focus().toggleBulletList().run(), () => editor.isActive('bulletList'))}
      {btn('ol', <OrderedListOutlined />, 'Нумерованный список', () => editor.chain().focus().toggleOrderedList().run(), () => editor.isActive('orderedList'))}
      {btn('quote', <span style={{ fontWeight: 600 }}>“”</span>, 'Цитата', () => editor.chain().focus().toggleBlockquote().run(), () => editor.isActive('blockquote'))}
      {btn('hr', <span>—</span>, 'Горизонтальная линия', () => editor.chain().focus().setHorizontalRule().run())}
      <Divider type="vertical" style={{ margin: '0 4px' }} />
      <Tooltip title="Ссылка">
        <Button
          size="small"
          type="text"
          icon={<LinkOutlined />}
          onClick={() => {
            const prev = (editor.getAttributes('link') as { href?: string }).href ?? '';
            const url = window.prompt('URL ссылки', prev);
            if (url === null) return; // cancel
            if (url === '') {
              editor.chain().focus().extendMarkRange('link').unsetLink?.().run();
            } else {
              editor.chain().focus().extendMarkRange('link').setLink?.({ href: url }).run();
            }
          }}
        />
      </Tooltip>
      <Divider type="vertical" style={{ margin: '0 4px' }} />
      {btn('undo', <UndoOutlined />, 'Отменить', () => editor.chain().focus().undo().run())}
      {btn('redo', <RedoOutlined />, 'Повторить', () => editor.chain().focus().redo().run())}
      <Divider type="vertical" style={{ margin: '0 4px' }} />
      {btn('task', <CheckSquareOutlined />, 'Чек-лист (если включён)', () => {
        const chain = editor.chain().focus();
        if ((chain as unknown as { toggleTaskList?: () => typeof chain }).toggleTaskList) {
          (chain as unknown as { toggleTaskList: () => typeof chain }).toggleTaskList().run();
        }
      })}
    </div>
  );
}
